/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * MobileOutboundDrawer — mobile sibling of OutboundPreview.tsx.
 *
 * Bottom-sheet card stack for the /chat Tailscale-companion app. One
 * vertical card per envelope held in state/outbound/*.json. Touch flow:
 *
 *   • Swipe right (>60px) → green RELEASE tint, then ships the draft
 *   • Swipe left  (>60px) → red KILL tint, then discards
 *   • Tap card body       → edit modal (textarea + Save / Save & Release / Cancel)
 *
 * Polls /api/outbound-preview/pending every 4s. Slides up from the bottom
 * like an iOS modal (parent renders it conditionally as an overlay).
 *
 * NOTE on the backend edit endpoint: the task brief mentioned
 * `PUT /api/outbound-preview/{filename}`, but the actual route is
 * `POST /api/outbound-preview/edit/{filename}` (see
 * src/api/routes/outbound_preview.py, line 202). We use what exists.
 *
 * TODO: PWA push notification on new draft — out of scope this turn.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  X,
  Check,
  Send,
  Trash2,
  RefreshCw,
  Pencil,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { OutboundPreviewItem, TerminalTheme } from "../types";

type ScopedFetch = (path: string, init?: RequestInit) => Promise<Response>;

interface MobileOutboundDrawerProps {
  theme: TerminalTheme;
  onClose: () => void;
  scopedFetch?: ScopedFetch;
  /** Polling cadence — 4s per spec. */
  pollMs?: number;
}

// Semantic action colors (match OutboundPreview.tsx convention).
const RELEASE_COLOR = "#10b981"; // emerald-500
const KILL_COLOR = "#f43f5e"; // rose-500
const SWIPE_THRESHOLD = 60; // px — past this, the gesture commits on release

const POLL_MS_DEFAULT = 4000;
const PREVIEW_LINES = 3;

function channelBadgeStyle(channel: string): {
  bg: string;
  border: string;
  fg: string;
  label: string;
} {
  switch (channel) {
    case "telegram":
      return {
        bg: "rgba(8,47,73,0.4)",
        border: "rgba(56,189,248,0.3)",
        fg: "#38bdf8",
        label: "Telegram",
      };
    case "slack":
      return {
        bg: "rgba(59,7,100,0.4)",
        border: "rgba(168,85,247,0.3)",
        fg: "#c084fc",
        label: "Slack",
      };
    case "imessage":
      return {
        bg: "rgba(6,78,59,0.4)",
        border: "rgba(16,185,129,0.3)",
        fg: "#34d399",
        label: "iMessage",
      };
    case "email":
      return {
        bg: "rgba(120,53,15,0.4)",
        border: "rgba(251,191,36,0.3)",
        fg: "#fbbf24",
        label: "Email",
      };
    case "sms":
      return {
        bg: "rgba(76,5,25,0.4)",
        border: "rgba(244,114,182,0.3)",
        fg: "#f472b6",
        label: "SMS",
      };
    case "dashboard":
      return { bg: "#27272a", border: "#3f3f46", fg: "#d4d4d8", label: "Dashboard" };
    default:
      return { bg: "#27272a", border: "#3f3f46", fg: "#d4d4d8", label: channel };
  }
}

/** Extract the recipient string from the envelope payload. Different
 * channels store it under different keys; we try the common ones in
 * priority order and fall back to a dash. */
function extractRecipient(payload: Record<string, any> | undefined): string {
  if (!payload) return "—";
  if (typeof payload.to === "string" && payload.to.trim()) return payload.to;
  if (payload.chat_id != null) return `chat ${payload.chat_id}`;
  if (typeof payload.recipient === "string" && payload.recipient.trim())
    return payload.recipient;
  if (typeof payload.email === "string" && payload.email.trim())
    return payload.email;
  if (typeof payload.phone === "string" && payload.phone.trim())
    return payload.phone;
  return "—";
}

/** Human-readable age string from an ISO timestamp. */
function ageString(iso: string | null | undefined, fallback: number): string {
  let secs = fallback;
  if (iso) {
    const t = Date.parse(iso);
    if (!Number.isNaN(t)) {
      secs = Math.max(0, Math.floor((Date.now() - t) / 1000));
    }
  }
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
  return `${Math.floor(secs / 86400)}d`;
}

// ─── Card ──────────────────────────────────────────────────────────────

interface DraftCardProps {
  item: OutboundPreviewItem;
  theme: TerminalTheme;
  onRelease: (filename: string) => void;
  onKill: (filename: string) => void;
  onEdit: (item: OutboundPreviewItem) => void;
  locked: boolean;
}

const DraftCard: React.FC<DraftCardProps> = ({
  item,
  theme,
  onRelease,
  onKill,
  onEdit,
  locked,
}) => {
  const [dx, setDx] = useState(0);
  const [animatingOff, setAnimatingOff] = useState<"left" | "right" | null>(
    null
  );
  const [expanded, setExpanded] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const horizontalLock = useRef<boolean | null>(null);

  const badge = useMemo(() => channelBadgeStyle(item.channel), [item.channel]);
  const recipient = useMemo(() => extractRecipient(item.payload), [item.payload]);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (locked || animatingOff) return;
    const t = e.touches[0];
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
    horizontalLock.current = null;
  }, [locked, animatingOff]);

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (locked || animatingOff) return;
      if (touchStartX.current == null || touchStartY.current == null) return;
      const t = e.touches[0];
      const deltaX = t.clientX - touchStartX.current;
      const deltaY = t.clientY - touchStartY.current;

      // First few px: decide if this is a horizontal swipe (commit gesture)
      // or a vertical scroll (let the scroll container handle it). Once
      // locked horizontal, stay horizontal for the rest of the gesture.
      if (horizontalLock.current === null) {
        if (Math.abs(deltaX) < 6 && Math.abs(deltaY) < 6) return;
        horizontalLock.current = Math.abs(deltaX) > Math.abs(deltaY);
      }
      if (!horizontalLock.current) return;

      // Prevent the surrounding scroll container from also handling this
      // gesture once we know it's a horizontal swipe.
      e.preventDefault?.();
      setDx(deltaX);
    },
    [locked, animatingOff]
  );

  const onTouchEnd = useCallback(() => {
    if (locked || animatingOff) {
      touchStartX.current = null;
      touchStartY.current = null;
      horizontalLock.current = null;
      return;
    }
    const committed = Math.abs(dx) >= SWIPE_THRESHOLD;
    if (committed && horizontalLock.current) {
      const direction: "left" | "right" = dx > 0 ? "right" : "left";
      setAnimatingOff(direction);
      // Allow the slide-off transition to play, then fire the action.
      // Parent removes the item from the list on success, so the card
      // unmounts naturally — we don't need to reset dx after.
      window.setTimeout(() => {
        if (direction === "right") onRelease(item.filename);
        else onKill(item.filename);
      }, 220);
    } else {
      // Snap back.
      setDx(0);
    }
    touchStartX.current = null;
    touchStartY.current = null;
    horizontalLock.current = null;
  }, [dx, locked, animatingOff, onRelease, onKill, item.filename]);

  // Tint the card progressively as it's dragged — green right, red left.
  const tintBg = useMemo(() => {
    if (Math.abs(dx) < 8) return theme.inputBg;
    const ratio = Math.min(1, Math.abs(dx) / 120);
    const color = dx > 0 ? RELEASE_COLOR : KILL_COLOR;
    // alpha 0x10 → 0x40 as ratio climbs
    const alphaHex = Math.floor(0x10 + ratio * 0x30)
      .toString(16)
      .padStart(2, "0");
    return `${color}${alphaHex}`;
  }, [dx, theme.inputBg]);

  const tintBorder = useMemo(() => {
    if (Math.abs(dx) < SWIPE_THRESHOLD) return theme.border;
    return dx > 0 ? RELEASE_COLOR : KILL_COLOR;
  }, [dx, theme.border]);

  const translateX = animatingOff === "right"
    ? "120%"
    : animatingOff === "left"
    ? "-120%"
    : `${dx}px`;

  const opacity = animatingOff
    ? 0
    : Math.max(0.55, 1 - Math.abs(dx) / 320);

  const showReleaseHint = dx >= SWIPE_THRESHOLD;
  const showKillHint = dx <= -SWIPE_THRESHOLD;

  const previewLineClamp: React.CSSProperties = expanded
    ? {}
    : {
        display: "-webkit-box",
        WebkitLineClamp: PREVIEW_LINES,
        WebkitBoxOrient: "vertical" as const,
        overflow: "hidden",
      };

  return (
    <div
      style={{
        backgroundColor: tintBg,
        borderColor: tintBorder,
        transform: `translateX(${translateX})`,
        opacity,
        transition:
          touchStartX.current == null
            ? "transform 220ms ease-out, opacity 220ms ease-out, background-color 120ms, border-color 120ms"
            : "background-color 60ms, border-color 60ms",
        touchAction: "pan-y",
      }}
      className="border rounded-2xl px-4 py-3 flex flex-col gap-2.5 relative overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
    >
      {/* Top chip row — channel badge, recipient, age */}
      <div className="flex items-center gap-2 flex-wrap">
        <span
          style={{
            backgroundColor: badge.bg,
            borderColor: badge.border,
            color: badge.fg,
          }}
          className="text-[11px] px-2 py-0.5 font-bold uppercase rounded-md border tracking-wide"
        >
          {badge.label}
        </span>
        <span
          style={{ color: theme.fg }}
          className="text-[13px] truncate max-w-[180px] font-medium"
          title={recipient}
        >
          {recipient}
        </span>
        <span
          style={{ color: theme.systemText }}
          className="text-[11px] ml-auto flex-shrink-0"
          title={item.ready_at || ""}
        >
          {ageString(item.ready_at, item.seconds_remaining || 0)}
        </span>
      </div>

      {/* Body preview — tap to expand */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="text-left w-full"
        style={{
          color: theme.fg,
          fontSize: "15px",
          lineHeight: "1.45",
          minHeight: "44px",
          ...previewLineClamp,
        }}
        aria-label={expanded ? "Collapse body" : "Expand body"}
      >
        {item.text || <span style={{ color: theme.systemText }}>(empty)</span>}
      </button>

      {/* Bottom action row — swipe hints + tap-to-edit */}
      <div
        className="flex items-center justify-between gap-2 pt-1"
        style={{ borderTop: `1px solid ${theme.border}` }}
      >
        <div
          className="flex items-center gap-1 text-[11px]"
          style={{ color: showKillHint ? KILL_COLOR : theme.systemText }}
        >
          <ChevronLeft size={14} />
          <span className="uppercase tracking-wide font-semibold">Kill</span>
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(item);
          }}
          disabled={locked}
          style={{
            color: theme.accent,
            borderColor: theme.border,
            backgroundColor: theme.bg,
            minHeight: "36px",
          }}
          className="border rounded-full px-4 flex items-center gap-1.5 text-[12px] font-semibold disabled:opacity-50"
          aria-label="Edit draft"
        >
          <Pencil size={13} /> Edit
        </button>

        <div
          className="flex items-center gap-1 text-[11px]"
          style={{ color: showReleaseHint ? RELEASE_COLOR : theme.systemText }}
        >
          <span className="uppercase tracking-wide font-semibold">Release</span>
          <ChevronRight size={14} />
        </div>
      </div>

      {/* Overlay tint labels when at threshold */}
      {showReleaseHint && (
        <div
          className="absolute top-3 right-4 px-2 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider pointer-events-none"
          style={{ backgroundColor: RELEASE_COLOR, color: "#fff" }}
        >
          Release
        </div>
      )}
      {showKillHint && (
        <div
          className="absolute top-3 left-4 px-2 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider pointer-events-none"
          style={{ backgroundColor: KILL_COLOR, color: "#fff" }}
        >
          Kill
        </div>
      )}
    </div>
  );
};

// ─── Edit modal ────────────────────────────────────────────────────────

interface EditModalProps {
  item: OutboundPreviewItem;
  theme: TerminalTheme;
  saving: boolean;
  onCancel: () => void;
  onSave: (text: string) => void;
  onSaveAndRelease: (text: string) => void;
}

const EditModal: React.FC<EditModalProps> = ({
  item,
  theme,
  saving,
  onCancel,
  onSave,
  onSaveAndRelease,
}) => {
  const [text, setText] = useState(item.text);
  const trimmed = text.trim();

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.55)" }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: theme.bg,
          color: theme.fg,
          borderColor: theme.border,
          paddingBottom: "calc(env(safe-area-inset-bottom) + 16px)",
          maxHeight: "90vh",
        }}
        className="w-full sm:max-w-md border-t sm:border rounded-t-3xl sm:rounded-2xl flex flex-col overflow-hidden"
      >
        <div
          className="flex items-center justify-between px-4 pt-4 pb-2 flex-shrink-0"
          style={{ borderBottom: `1px solid ${theme.border}` }}
        >
          <div
            style={{ color: theme.fg }}
            className="text-base font-semibold flex items-center gap-2"
          >
            <Pencil size={16} style={{ color: theme.accent }} />
            Edit draft
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            style={{ color: theme.systemText, minWidth: "44px", minHeight: "44px" }}
            className="rounded-full flex items-center justify-center disabled:opacity-50"
            aria-label="Cancel edit"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-4 py-3 flex-1 min-h-0 overflow-y-auto">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={8}
            disabled={saving}
            autoFocus
            style={{
              backgroundColor: theme.inputBg,
              color: theme.fg,
              borderColor: theme.border,
              fontSize: "16px",
              lineHeight: "1.5",
              minHeight: "180px",
            }}
            className="w-full border rounded-xl p-3 focus:outline-none resize-none disabled:opacity-60"
            maxLength={8000}
            aria-label="Draft body"
          />
          <div
            className="flex items-center justify-between mt-1 text-[11px]"
            style={{ color: theme.systemText }}
          >
            <span>{text.length}/8000</span>
            <span>{item.channel} → editing</span>
          </div>
        </div>

        <div
          className="px-4 pt-3 flex flex-col gap-2 flex-shrink-0"
          style={{ borderTop: `1px solid ${theme.border}` }}
        >
          <button
            type="button"
            onClick={() => onSaveAndRelease(trimmed)}
            disabled={saving || !trimmed}
            style={{
              backgroundColor: RELEASE_COLOR,
              color: "#fff",
              minHeight: "48px",
              opacity: saving || !trimmed ? 0.55 : 1,
            }}
            className="rounded-full flex items-center justify-center gap-2 font-semibold text-[15px]"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            Save & Release
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onSave(trimmed)}
              disabled={saving || !trimmed}
              style={{
                backgroundColor: theme.inputBg,
                color: theme.fg,
                borderColor: theme.border,
                minHeight: "44px",
              }}
              className="border rounded-full flex-1 flex items-center justify-center gap-2 font-semibold text-[14px] disabled:opacity-50"
            >
              <Check size={15} /> Save
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              style={{
                backgroundColor: "transparent",
                color: theme.systemText,
                borderColor: theme.border,
                minHeight: "44px",
              }}
              className="border rounded-full flex-1 flex items-center justify-center gap-2 font-semibold text-[14px] disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Drawer (bottom sheet) ─────────────────────────────────────────────

export const MobileOutboundDrawer: React.FC<MobileOutboundDrawerProps> = ({
  theme,
  onClose,
  scopedFetch,
  pollMs = POLL_MS_DEFAULT,
}) => {
  const fetcher: ScopedFetch = useMemo(
    () => scopedFetch ?? ((path, init) => fetch(path, init)),
    [scopedFetch]
  );

  const [items, setItems] = useState<OutboundPreviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<OutboundPreviewItem | null>(
    null
  );
  const [actionLockFor, setActionLockFor] = useState<string | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [entered, setEntered] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    // Trigger the slide-up animation on next frame.
    const id = window.requestAnimationFrame(() => setEntered(true));
    return () => {
      mountedRef.current = false;
      window.cancelAnimationFrame(id);
    };
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await fetcher("/api/outbound-preview/pending");
      if (!res.ok) {
        if (mountedRef.current) setError(`pending: HTTP ${res.status}`);
        return;
      }
      const data = await res.json();
      if (!mountedRef.current) return;
      setItems(Array.isArray(data?.items) ? data.items : []);
      setError(null);
    } catch (e: any) {
      if (!mountedRef.current) return;
      setError(e?.message || "fetch failed");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [fetcher]);

  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, pollMs);
    return () => window.clearInterval(id);
  }, [refresh, pollMs]);

  const handleClose = useCallback(() => {
    // Play the slide-down animation before unmounting.
    setEntered(false);
    window.setTimeout(() => onClose(), 220);
  }, [onClose]);

  // ─── Mutations ────────────────────────────────────────────────────
  const post = useCallback(
    (path: string, body?: object) =>
      fetcher(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: body ? JSON.stringify(body) : undefined,
      }),
    [fetcher]
  );

  const doRelease = useCallback(
    async (filename: string) => {
      setActionLockFor(filename);
      // Optimistically remove the card — the swipe animation has already
      // played; the server call is post-hoc. If it fails, refresh repopulates.
      setItems((prev) => prev.filter((i) => i.filename !== filename));
      try {
        const res = await post(
          `/api/outbound-preview/release/${encodeURIComponent(filename)}`
        );
        if (!res.ok) setError(`release: HTTP ${res.status}`);
      } catch (e: any) {
        setError(e?.message || "release failed");
      } finally {
        setActionLockFor(null);
        refresh();
      }
    },
    [post, refresh]
  );

  const doKill = useCallback(
    async (filename: string) => {
      setActionLockFor(filename);
      setItems((prev) => prev.filter((i) => i.filename !== filename));
      try {
        const res = await post(
          `/api/outbound-preview/kill/${encodeURIComponent(filename)}`
        );
        if (!res.ok) setError(`kill: HTTP ${res.status}`);
      } catch (e: any) {
        setError(e?.message || "kill failed");
      } finally {
        setActionLockFor(null);
        refresh();
      }
    },
    [post, refresh]
  );

  const doEdit = useCallback(
    async (filename: string, text: string): Promise<boolean> => {
      setSavingEdit(true);
      try {
        const res = await post(
          `/api/outbound-preview/edit/${encodeURIComponent(filename)}`,
          { text }
        );
        if (!res.ok) {
          setError(`edit: HTTP ${res.status}`);
          return false;
        }
        return true;
      } catch (e: any) {
        setError(e?.message || "edit failed");
        return false;
      } finally {
        setSavingEdit(false);
      }
    },
    [post]
  );

  const handleSave = useCallback(
    async (text: string) => {
      if (!editingItem || !text.trim()) return;
      const ok = await doEdit(editingItem.filename, text);
      if (ok) {
        setEditingItem(null);
        refresh();
      }
    },
    [editingItem, doEdit, refresh]
  );

  const handleSaveAndRelease = useCallback(
    async (text: string) => {
      if (!editingItem || !text.trim()) return;
      const target = editingItem.filename;
      const ok = await doEdit(target, text);
      if (ok) {
        setEditingItem(null);
        await doRelease(target);
      }
    },
    [editingItem, doEdit, doRelease]
  );

  // ─── Render ──────────────────────────────────────────────────────
  const pendingCount = items.length;

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col"
      style={{
        backgroundColor: entered ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0)",
        transition: "background-color 220ms ease-out",
      }}
      onClick={handleClose}
      role="dialog"
      aria-label="Pending outbound drafts"
    >
      <div className="flex-1" />
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: theme.bg,
          color: theme.fg,
          borderTopColor: theme.border,
          maxHeight: "92vh",
          minHeight: "60vh",
          paddingBottom: "env(safe-area-inset-bottom)",
          transform: entered ? "translateY(0)" : "translateY(100%)",
          transition: "transform 240ms cubic-bezier(0.32, 0.72, 0, 1)",
        }}
        className="border-t rounded-t-3xl flex flex-col overflow-hidden"
      >
        {/* Grabber pill */}
        <div className="pt-2 pb-1 flex justify-center flex-shrink-0">
          <div
            style={{ backgroundColor: theme.border }}
            className="h-1 w-12 rounded-full"
          />
        </div>

        {/* Header — title + badge + refresh + close */}
        <div
          className="flex items-center justify-between px-4 py-3 flex-shrink-0"
          style={{ borderBottom: `1px solid ${theme.border}` }}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div
              style={{
                backgroundColor: theme.accent,
                color: theme.bg,
                width: 36,
                height: 36,
              }}
              className="rounded-full flex items-center justify-center flex-shrink-0"
            >
              <Send size={16} />
            </div>
            <div className="min-w-0">
              <div
                style={{ color: theme.fg }}
                className="font-semibold text-base flex items-center gap-2"
              >
                Drafts
                <span
                  style={{
                    backgroundColor:
                      pendingCount > 0 ? `${theme.accent}28` : theme.inputBg,
                    color: pendingCount > 0 ? theme.accent : theme.systemText,
                    borderColor:
                      pendingCount > 0 ? `${theme.accent}55` : theme.border,
                  }}
                  className="text-[11px] border rounded-full px-2 py-0.5 font-bold tabular-nums min-w-[28px] text-center"
                  aria-label={`${pendingCount} drafts pending`}
                >
                  {pendingCount}
                </span>
              </div>
              <div
                style={{ color: theme.systemText }}
                className="text-[12px] truncate"
              >
                Swipe right to release. Left to kill. Tap to edit.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => {
                setLoading(true);
                refresh();
              }}
              style={{
                color: theme.systemText,
                minWidth: "44px",
                minHeight: "44px",
              }}
              className="rounded-full flex items-center justify-center hover:opacity-70"
              aria-label="Refresh drafts"
              title="Refresh"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              type="button"
              onClick={handleClose}
              style={{
                color: theme.systemText,
                minWidth: "44px",
                minHeight: "44px",
              }}
              className="rounded-full flex items-center justify-center hover:opacity-70"
              aria-label="Close drafts panel"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div
            style={{
              backgroundColor: `${KILL_COLOR}1a`,
              borderBottomColor: `${KILL_COLOR}55`,
              color: KILL_COLOR,
            }}
            className="border-b px-4 py-2 text-[12px] flex items-center justify-between gap-2 flex-shrink-0"
          >
            <span className="truncate">{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              style={{ color: KILL_COLOR, minWidth: "32px", minHeight: "32px" }}
              className="rounded-full flex items-center justify-center flex-shrink-0"
              aria-label="Dismiss error"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Card stack — scrollable region */}
        <div
          className="flex-1 overflow-y-auto px-3 py-3 min-h-0"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {loading && items.length === 0 ? (
            <div
              className="flex flex-col items-center justify-center py-16 gap-3"
              style={{ color: theme.systemText }}
            >
              <Loader2 size={28} className="animate-spin" />
              <div className="text-[14px]">Loading drafts…</div>
            </div>
          ) : items.length === 0 ? (
            <div
              className="flex flex-col items-center justify-center py-16 gap-3 px-6 text-center"
              style={{ color: theme.systemText }}
            >
              <div
                style={{
                  backgroundColor: `${RELEASE_COLOR}1a`,
                  color: RELEASE_COLOR,
                  width: 64,
                  height: 64,
                }}
                className="rounded-full flex items-center justify-center"
              >
                <Check size={28} />
              </div>
              <div
                style={{ color: theme.fg, fontSize: "16px" }}
                className="font-semibold"
              >
                Nothing waiting — Margo's got it.
              </div>
              <div className="text-[13px]" style={{ color: theme.systemText }}>
                Drafts will appear here when Margo holds something for your
                approval.
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {items.map((item) => (
                <DraftCard
                  key={item.id}
                  item={item}
                  theme={theme}
                  onRelease={doRelease}
                  onKill={doKill}
                  onEdit={(it) => setEditingItem(it)}
                  locked={actionLockFor === item.filename}
                />
              ))}
              <div
                className="text-center py-3 text-[11px]"
                style={{ color: theme.systemText }}
              >
                <Trash2 size={12} className="inline mr-1" /> Killed drafts move
                to{" "}
                <code style={{ color: theme.fg }}>state/outbound/cancelled/</code>
              </div>
            </div>
          )}
        </div>
      </div>

      {editingItem && (
        <EditModal
          item={editingItem}
          theme={theme}
          saving={savingEdit}
          onCancel={() => setEditingItem(null)}
          onSave={handleSave}
          onSaveAndRelease={handleSaveAndRelease}
        />
      )}
    </div>
  );
};
